// src/pages/HeathrowInfo.jsx
import React from "react";
import { Link } from "react-router-dom";
import "../styles/heathrow-info.css";
import { Helmet } from "react-helmet";

const HeathrowInfo = () => {
  return (
    <div className="heathrow-info-page">
      <Helmet>
        <title>Heathrow Airport Information & Transfers | CheapCabGrab</title>
        <meta
          name="description"
          content="Essential Heathrow Airport information in one place – terminals, drop-off and pick-up rules, parking, transfers and travel tips. Book reliable Heathrow taxis with CheapCabGrab."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow" />
      </Helmet>

      {/* HERO */}
      <header className="heathrow-info-hero">
        <div className="heathrow-info-hero-inner">
          <h1>🛫 Heathrow Airport Information</h1>
          <p>
            Quick, practical Heathrow guidance – terminals, drop-off and pick-up,
            parking and transfers – plus fast, reliable taxis with{" "}
            <strong>CheapCabGrab</strong> to and from Heathrow.
          </p>

          <div className="heathrow-info-hero-ctas">
            <Link to="/heathrow-taxi" className="heathrow-primary-btn">
              🚖 Book Heathrow Taxi
            </Link>
            <Link to="/terminalfinder" className="heathrow-secondary-btn">
              🛬 Find Airline Terminal
            </Link>
          </div>
        </div>
      </header>

      {/* IN-PAGE NAV */}
      <nav className="heathrow-info-subnav">
        <a href="#overview">Overview</a>
        <a href="#terminals">Terminals</a>
        <a href="#dropoff-pickup">Drop-off & Pick-up</a>
        <a href="#parking">Parking</a>
        <a href="#transfers">Getting Around</a>
        <a href="#security-baggage">Security & Baggage</a>
        <a href="#assistance">Help & Complaints</a>
        <a href="#tools">Useful Tools</a>
        <a href="#faqs">FAQs</a>
      </nav>

      <main className="heathrow-info-main">
        {/* OVERVIEW */}
        <section id="overview" className="heathrow-section">
          <h2>✈️ Heathrow at a glance</h2>
          <div className="heathrow-two-col">
            <div className="heathrow-text">
              <p>
                Heathrow is one of the world’s busiest international airports,
                with four passenger terminals (2, 3, 4 and 5) and excellent
                road links into <strong>London, Slough, Windsor, Maidenhead</strong>{" "}
                and the wider Thames Valley.
              </p>
              <p>
                CheapCabGrab specialises in <strong>fixed-price Heathrow taxi
                transfers</strong> for solo travellers, families and groups – 24/7,
                with flight tracking and meet-and-greet available.
              </p>
            </div>
            <div className="heathrow-highlight-card">
              <h3>Why use CheapCabGrab to/from Heathrow?</h3>
              <ul>
                <li>✅ Fixed, upfront prices – no surprise surcharges</li>
                <li>✅ Licensed, DBS-checked drivers</li>
                <li>✅ Flight tracking for most major airlines</li>
                <li>✅ Saloon, MPV, 8–14 seater minibuses & coach options</li>
              </ul>
              <Link to="/heathrow-taxi" className="heathrow-inline-link">
                Get a Heathrow taxi quote →
              </Link>
            </div>
          </div>
        </section>

        {/* TERMINALS */}
        <section id="terminals" className="heathrow-section">
          <h2>🏢 Heathrow terminals 2, 3, 4 & 5</h2>
          <div className="heathrow-card-grid">
            <div className="heathrow-card">
              <h3>Terminal 2 (The Queen’s Terminal)</h3>
              <p>
                Mainly Star Alliance and selected long-haul airlines. Modern
                terminal with central arrivals hall and good public transport
                links.
              </p>
              <ul>
                <li>🛬 Central arrivals meeting point</li>
                <li>🚇 Direct access to Underground & Heathrow Express</li>
                <li>🅿️ Short-stay car park opposite the terminal</li>
              </ul>
            </div>

            <div className="heathrow-card">
              <h3>Terminal 3</h3>
              <p>
                Serves a mix of long-haul carriers, including many North
                American flights.
              </p>
              <ul>
                <li>🛬 Arrivals hall on ground level</li>
                <li>🛍️ Strong mix of shops, cafés and lounges</li>
                <li>🚶 Short walk from T2 for connections</li>
              </ul>
            </div>

            <div className="heathrow-card">
              <h3>Terminal 4</h3>
              <p>
                Located on the south side of the airport, used by a number of
                European and long-haul airlines.
              </p>
              <ul>
                <li>🚆 Served by Piccadilly Line & Elizabeth Line</li>
                <li>🚌 Local hotel shuttle buses</li>
                <li>🚖 CheapCabGrab coverage for hotel & business parks nearby</li>
              </ul>
            </div>

            <div className="heathrow-card">
              <h3>Terminal 5</h3>
              <p>
                Flagship terminal used mainly by British Airways and partner
                airlines, with its own rail and road access.
              </p>
              <ul>
                <li>🚆 Direct Heathrow Express & Elizabeth Line to London</li>
                <li>🛍️ Large choice of shops, dining & lounges</li>
                <li>
                  🚖 Dedicated CheapCabGrab pick-up points at hotel & car parks
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* DROPOFF / PICKUP */}
        <section id="dropoff-pickup" className="heathrow-section">
          <h2>🚗 Drop-off & pick-up at Heathrow</h2>
          <div className="heathrow-two-col">
            <div className="heathrow-text">
              <h3>Terminal drop-off zones</h3>
              <p>
                Heathrow operates dedicated drop-off areas directly outside each
                terminal. A small charge applies for most vehicles using these
                zones – always check the latest fees and exemptions on the
                official Heathrow site before you travel.
              </p>
              <p>
                With <strong>CheapCabGrab</strong>, your driver will drop you in the
                correct terminal zone and help with luggage, so you can head
                straight to check-in.
              </p>
            </div>
            <div className="heathrow-text">
              <h3>Pick-up rules</h3>
              <p>
                Terminal forecourts are generally <strong>no-waiting</strong> areas.
                Pick-ups are usually made from:
              </p>
              <ul>
                <li>🅿️ Short-stay car parks, signposted at each terminal</li>
                <li>🚖 Designated taxi / private hire bays</li>
              </ul>
              <p>
                Your CheapCabGrab driver will confirm the exact meeting point in
                your booking confirmation (arrivals hall or short-stay car park,
                depending on your preference).
              </p>
            </div>
          </div>
        </section>

        {/* PARKING */}
        <section id="parking" className="heathrow-section">
          <h2>🅿️ Parking options at Heathrow</h2>
          <div className="heathrow-card-grid">
            <div className="heathrow-card">
              <h3>Short-stay parking</h3>
              <p>
                Multi-storey car parks close to each terminal, ideal for
                drop-offs, quick pick-ups and short business trips.
              </p>
              <ul>
                <li>⏱ Usually from 0–24 hours</li>
                <li>💷 Charged by the hour</li>
                <li>🚶 Short walk to departures/arrivals</li>
              </ul>
            </div>

            <div className="heathrow-card">
              <h3>Long-stay & business parking</h3>
              <p>
                Located further from terminals with shuttle buses running every
                few minutes.
              </p>
              <ul>
                <li>💼 Good for holidays and longer trips</li>
                <li>🚍 Free shuttle bus to terminals</li>
                <li>💷 Pre-booking usually cheaper</li>
              </ul>
            </div>

            <div className="heathrow-card">
              <h3>Meet & Greet / valet</h3>
              <p>
                Drop your car with a valet close to the terminal and collect it
                on return.
              </p>
              <ul>
                <li>👍 Convenience for families & business travellers</li>
                <li>📅 Must be pre-booked</li>
                <li>🚖 CheapCabGrab available for one-way journeys if you prefer not to park</li>
              </ul>
            </div>
          </div>
        </section>

        {/* TRANSFERS */}
        <section id="transfers" className="heathrow-section">
          <h2>🚉 Getting between Heathrow & nearby areas</h2>
          <div className="heathrow-two-col">
            <div className="heathrow-text">
              <h3>Between terminals</h3>
              <p>
                Transfer between terminals is possible using{" "}
                <strong>Heathrow Express, Elizabeth Line, Piccadilly Line</strong>{" "}
                or dedicated shuttle services (availability can change, so always
                check signs and airport information).
              </p>
              <p>
                For groups or passengers with heavy luggage,{" "}
                <strong>CheapCabGrab</strong> can provide direct terminal-to-terminal
                transfers, including minibus options for up to 14 passengers.
              </p>
            </div>
            <div className="heathrow-text">
              <h3>Heathrow to London & beyond</h3>
              <p>Common CheapCabGrab routes include:</p>
              <ul>
                <li>🚖 Heathrow ↔ Central London hotels & stations</li>
                <li>🚖 Heathrow ↔ Slough, Windsor, Maidenhead & Reading</li>
                <li>🚖 Heathrow ↔ Gatwick, Luton & Stansted (airport-to-airport)</li>
              </ul>
              <Link to="/services" className="heathrow-inline-link">
                See all CheapCabGrab airport routes →
              </Link>
            </div>
          </div>
        </section>

        {/* SECURITY & BAGGAGE */}
        <section id="security-baggage" className="heathrow-section">
          <h2>🛂 Security, liquids & baggage at Heathrow</h2>
          <div className="heathrow-two-col">
            <div className="heathrow-text">
              <h3>Security & liquids</h3>
              <p>
                Security screening rules can change over time as new equipment
                is installed. Always check your airline’s and Heathrow’s latest
                guidance on liquids, laptops and cabin baggage <em>before</em>{" "}
                you travel.
              </p>
              <p>
                Arrive in good time – especially at busy periods – and keep
                liquids and electronics easy to remove from your bag if needed.
              </p>
            </div>
            <div className="heathrow-text">
              <h3>Checked baggage</h3>
              <p>
                Baggage allowances vary by airline and ticket type. Check your
                booking confirmation before packing and make sure your bags are
                clearly labelled with your name and contact details.
              </p>
              <p>
                CheapCabGrab vehicles have options for{" "}
                <strong>extra luggage, sports equipment and pushchairs</strong> –
                just tell us your needs when you book.
              </p>
            </div>
          </div>
        </section>

        {/* ASSISTANCE & COMPLAINTS */}
        <section id="assistance" className="heathrow-section">
          <h2>🧑‍🦽 Special assistance & complaints</h2>
          <div className="heathrow-two-col">
            <div className="heathrow-text">
              <h3>Special assistance at Heathrow</h3>
              <p>
                Heathrow offers dedicated services for passengers who need
                additional help – including mobility support, hidden disability
                lanyards and assistance through security.
              </p>
              <p>
                Request assistance through your airline in advance of travel,
                then let <strong>CheapCabGrab</strong> know about any requirements
                (wheelchairs, mobility aids, extra time for boarding) so we can
                plan the right vehicle and pick-up point.
              </p>
            </div>
            <div className="heathrow-text">
              <h3>Raising a complaint</h3>
              <p>
                If you have an issue with airport facilities, security,
                baggage or terminal services, complaints must normally be made
                directly to <strong>Heathrow Airport</strong> or your airline.
              </p>
              <p>
                For anything related to your{" "}
                <strong>CheapCabGrab journey, driver or payment</strong>, please
                contact our support team and we’ll investigate promptly.
              </p>
              <Link to="/contact" className="heathrow-inline-link">
                Contact CheapCabGrab support →
              </Link>
            </div>
          </div>
        </section>

        {/* TOOLS & LINKS */}
        <section id="tools" className="heathrow-section">
          <h2>🧰 Helpful Heathrow tools</h2>
          <div className="heathrow-card-grid">
            <div className="heathrow-card">
              <h3>🛫 Terminal Finder</h3>
              <p>
                Not sure which terminal your airline uses? Search by airline and
                see the matching terminal.
              </p>
              <Link to="/terminalfinder" className="heathrow-inline-link">
                Open Terminal Finder →
              </Link>
            </div>

            <div className="heathrow-card">
              <h3>📞 Airline Contact Details</h3>
              <p>
                Quick contact information for airlines operating at Heathrow,
                including customer service numbers and websites.
              </p>
              <Link to="/airlineContacts" className="heathrow-inline-link">
                View airline contacts →
              </Link>
            </div>

            <div className="heathrow-card">
              <h3>🚖 Heathrow Taxi Quotes</h3>
              <p>
                Instant fixed-price quotes for Heathrow transfers with
                CheapCabGrab – no surge pricing, no hidden fees.
              </p>
              <Link to="/heathrow-taxi" className="heathrow-inline-link">
                Get Heathrow taxi fare →
              </Link>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section id="faqs" className="heathrow-section">
          <h2>❓ Heathrow & CheapCabGrab FAQs</h2>
          <div className="heathrow-faq-grid">
            <div className="heathrow-faq-card">
              <h3>How early should I arrive for my flight?</h3>
              <p>
                Most airlines recommend arriving <strong>2 hours before</strong>{" "}
                European flights and <strong>3 hours before</strong> long-haul
                flights. During busy periods, allow extra time for security and
                travel to the terminal.
              </p>
            </div>

            <div className="heathrow-faq-card">
              <h3>Can CheapCabGrab track my flight?</h3>
              <p>
                Yes, for most major airlines we track your flight number so we
                know if you are early or delayed. Standard waiting time is
                included – we’ll confirm details in your booking.
              </p>
            </div>

            <div className="heathrow-faq-card">
              <h3>Where will my driver meet me?</h3>
              <p>
                Usually in the <strong>arrivals hall</strong> with a name board
                or at a pre-agreed point in the short-stay car park. You’ll
                receive full instructions in your confirmation email.
              </p>
            </div>

            <div className="heathrow-faq-card">
              <h3>Do you offer child seats or boosters?</h3>
              <p>
                Child seats can usually be provided on request. Please add
                details (age and number of children) in the booking notes so we
                can allocate the right vehicle.
              </p>
            </div>
          </div>
          <Link to="/faq" className="heathrow-inline-link">
            See all CheapCabGrab FAQs →
          </Link>
        </section>
      </main>
    </div>
  );
};

export default HeathrowInfo;

